type DeferredValueType<D> = D extends Deferred<infer T> ? T : never

/**
 * Wrapper that creates a `Promise` and allows you to reject or resolve it at a
 * later point. Use when you need a Promise but want to lazily evaluate the result
 */
export class Deferred<T = any> {
  resolve: (value?: T) => void
  reject: (reason: any) => void
  promise: Promise<T>
  isResolved = false
  isRejected = false
  constructor() {
    this.promise = new Promise<T>((resolve, reject) => {
      this.resolve = (value) => {
        this.isResolved = true
        resolve(value)
      }
      this.reject = (reason) => {
        this.isRejected = true
        reject(reason)
      }
    })
  }
}

/**
 * Groups `Defer` and `Promise` instances and provides some convenience methods
 * for working with them as a group
 */
export class Queue<T extends { [key: string]: Deferred | Promise<any> }> {
  completed = 0
  total = 0
  promise: Promise<any[]>
  /** Single callback, invoked when each item is resolved */
  onResolve?: (key: keyof T, value: any) => void
  /** Updates progress and fires callback when an item is resolved */
  complete(key: keyof T, value: any) {
    this.completed++
    if (this.onResolve) {
      this.onResolve(key, value)
    }
  }
  /** Convenience method for resolving child `Deferred` items */
  resolve<K extends keyof T>(key: K, value?: DeferredValueType<T[K]>) {
    const instance = this.queue[key]
    if (instance instanceof Deferred) {
      instance.resolve(value)
    } else {
      // eslint-disable-next-line
      console.warn(`Can't resolve the Promise named "${String(key)}", use Deferred instead`)
    }
  }
  /** Convenience method for rejecting child `Deferred` items */
  reject<K extends keyof T>(key: K, reason: any) {
    const instance = this.queue[key]
    if (instance instanceof Deferred) {
      instance.reject(reason)
    } else {
      // eslint-disable-next-line
      console.warn(`Can't reject the Promise named "${String(key)}", use Deferred instead`)
    }
  }

  constructor(private queue: T) {
    const entries = Object.entries(queue)
    this.total = entries.length
    this.promise = Promise.all(
      entries.map(([key, instance]) => {
        const promise = instance instanceof Deferred ? instance.promise : instance
        promise.then((value) => {
          this.complete(key, value)
          return value
        })
        return promise
      })
    )
  }
}
